import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Users,
  MapPin,
  AlertTriangle,
  RefreshCw,
  FileCheck,
  Filter,
} from 'lucide-react';
import OfficerAvatar from '@/components/common/OfficerAvatar';

export default function TimesheetApprovalsPage() {
  const { api, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [sites, setSites] = useState([]);

  // Filters
  const [siteFilter, setSiteFilter] = useState('all');

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingIds, setRejectingIds] = useState([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/timesheets/pending-approvals';
      if (siteFilter !== 'all') {
        url += `?site_id=${siteFilter}`;
      }
      const response = await api.get(url);
      // Handle both wrapped and unwrapped responses
      const data = response.data?.data || response.data || [];
      setTimesheets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view approvals');
      } else {
        toast.error('Failed to load pending approvals');
      }
    } finally {
      setLoading(false);
    }
  }, [api, siteFilter]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites?status=active&limit=200');
      // Handle both wrapped and unwrapped responses
      const data = response.data?.data || response.data || [];
      setSites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchPendingApprovals();
    fetchSites();
  }, [fetchPendingApprovals, fetchSites]);

  const handleApprove = async (id) => {
    try {
      setProcessing(true);
      await api.post(`/timesheets/${id}/approve`, { action: 'approve' });
      toast.success('Timesheet approved');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (ids) => {
    setRejectingIds(Array.isArray(ids) ? ids : [ids]);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      
      if (rejectingIds.length === 1) {
        await api.post(`/timesheets/${rejectingIds[0]}/approve`, {
          action: 'reject',
          reason: rejectionReason,
        });
        toast.success('Timesheet rejected');
      } else {
        const response = await api.post('/timesheets/bulk-approve', {
          timesheet_ids: rejectingIds,
          action: 'reject',
          reason: rejectionReason,
        });
        if (response.data.success > 0) {
          toast.success(`${response.data.success} timesheets rejected`);
        }
        if (response.data.failed > 0) {
          toast.error(`${response.data.failed} failed`);
        }
      }

      setShowRejectDialog(false);
      setRejectingIds([]);
      setSelectedIds(new Set());
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject timesheet');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select timesheets to approve');
      return;
    }

    try {
      setProcessing(true);
      const response = await api.post('/timesheets/bulk-approve', {
        timesheet_ids: Array.from(selectedIds),
        action: 'approve',
      });

      if (response.data.success > 0) {
        toast.success(`${response.data.success} timesheets approved`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} failed: ${response.data.errors?.join(', ')}`);
      }

      setSelectedIds(new Set());
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error bulk approving:', error);
      toast.error('Failed to approve timesheets');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = () => {
    if (selectedIds.size === 0) {
      toast.error('Please select timesheets to reject');
      return;
    }
    openRejectDialog(Array.from(selectedIds));
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(new Set(timesheets.map(ts => ts.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const formatTime = (isoString) => {
    if (!isoString) return '—';
    try {
      // Use 24-hour format consistently
      return new Date(isoString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
      return '—';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Parse YYYY-MM-DD as local date to avoid timezone shift
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const localDate = new Date(year, month, day);
        return localDate.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
      }
      return new Date(dateStr).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  // Group timesheets by user for better UX
  const groupedByUser = timesheets.reduce((acc, ts) => {
    const key = ts.user_id;
    if (!acc[key]) {
      acc[key] = {
        user_id: ts.user_id,
        user_name: ts.user_name,
        employee_id: ts.employee_id,
        entries: [],
        total_hours: 0,
        overtime_hours: 0,
      };
    }
    acc[key].entries.push(ts);
    acc[key].total_hours += ts.total_hours || 0;
    acc[key].overtime_hours += ts.overtime_hours || 0;
    return acc;
  }, {});

  const userGroups = Object.values(groupedByUser).sort((a, b) => 
    a.user_name?.localeCompare(b.user_name)
  );

  return (
    <div className="space-y-6" data-testid="timesheet-approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white">Timesheet Approvals</h1>
          <p className="text-zinc-400 text-sm">Review and approve submitted timesheets</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <FileCheck className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Pending</p>
                <p className="text-lg font-bold text-white">{timesheets.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Staff</p>
                <p className="text-lg font-bold text-white">{userGroups.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Total Hours</p>
                <p className="text-lg font-bold text-white">
                  {timesheets.reduce((sum, ts) => sum + (ts.total_hours || 0), 0).toFixed(1)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Overtime</p>
                <p className="text-lg font-bold text-white">
                  {timesheets.reduce((sum, ts) => sum + (ts.overtime_hours || 0), 0).toFixed(1)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-zinc-400" />
                <Select value={siteFilter} onValueChange={setSiteFilter}>
                  <SelectTrigger className="w-[200px] bg-[#09090b] border-[#3f3f46]">
                    <SelectValue placeholder="All Sites" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sites</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPendingApprovals}
                className="border-zinc-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-400">{selectedIds.size} selected</span>
                <Button
                  onClick={handleBulkApprove}
                  disabled={processing}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Approve
                </Button>
                <Button
                  onClick={handleBulkReject}
                  disabled={processing}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timesheets by User */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : userGroups.length === 0 ? (
        <Card className="bg-[#1a1a1d] border-[#3f3f46]">
          <CardContent className="py-12">
            <div className="text-center text-zinc-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500 opacity-50" />
              <p>No timesheets pending approval</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {userGroups.map((group) => (
            <Card key={group.user_id} className="bg-[#1a1a1d] border-[#3f3f46]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <OfficerAvatar
                      user={{
                        name: group.user_name,
                        full_name: group.user_name,
                        profile_image: group.profile_image,
                      }}
                      size="md"
                    />
                    <div>
                      <CardTitle className="font-rajdhani text-lg">{group.user_name}</CardTitle>
                      {group.employee_id && (
                        <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-500">
                          {group.employee_id}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-right">
                      <p className="text-zinc-500">Hours</p>
                      <p className="font-medium text-white">{group.total_hours.toFixed(1)}</p>
                    </div>
                    {group.overtime_hours > 0 && (
                      <div className="text-right">
                        <p className="text-zinc-500">OT</p>
                        <p className="font-medium text-orange-400">{group.overtime_hours.toFixed(1)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#3f3f46]">
                        <th className="text-left py-2 px-2 w-10">
                          <Checkbox
                            checked={group.entries.every(e => selectedIds.has(e.id))}
                            onCheckedChange={(checked) => {
                              const newSet = new Set(selectedIds);
                              group.entries.forEach(e => {
                                if (checked) {
                                  newSet.add(e.id);
                                } else {
                                  newSet.delete(e.id);
                                }
                              });
                              setSelectedIds(newSet);
                            }}
                          />
                        </th>
                        <th className="text-left py-2 px-2 text-zinc-400 font-medium">Date</th>
                        <th className="text-left py-2 px-2 text-zinc-400 font-medium">Site</th>
                        <th className="text-center py-2 px-2 text-zinc-400 font-medium">Time</th>
                        <th className="text-center py-2 px-2 text-zinc-400 font-medium">Break</th>
                        <th className="text-right py-2 px-2 text-zinc-400 font-medium">Hours</th>
                        <th className="text-right py-2 px-2 text-zinc-400 font-medium">OT</th>
                        <th className="text-center py-2 px-2 text-zinc-400 font-medium">Source</th>
                        <th className="text-right py-2 px-2 text-zinc-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.entries.map((ts) => (
                        <tr key={ts.id} className="border-b border-[#3f3f46]/30 hover:bg-[#27272a]">
                          <td className="py-2 px-2">
                            <Checkbox
                              checked={selectedIds.has(ts.id)}
                              onCheckedChange={() => toggleSelection(ts.id)}
                            />
                          </td>
                          <td className="py-2 px-2 text-white">{formatDate(ts.date)}</td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-zinc-500" />
                              <span className="text-zinc-300">{ts.site_name || '—'}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-center text-zinc-300">
                            {formatTime(ts.clock_in)} - {formatTime(ts.clock_out)}
                          </td>
                          <td className="py-2 px-2 text-center text-zinc-400">{ts.break_minutes || 0}m</td>
                          <td className="py-2 px-2 text-right font-medium text-white">{ts.total_hours?.toFixed(1)}</td>
                          <td className="py-2 px-2 text-right">
                            {ts.overtime_hours > 0 ? (
                              <span className="text-orange-400">{ts.overtime_hours.toFixed(1)}</span>
                            ) : (
                              <span className="text-zinc-600">—</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-500">
                              {ts.source === 'manual' ? 'Manual' : ts.source === 'roster_auto' ? 'Roster' : 'Route'}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(ts.id)}
                                disabled={processing}
                                className="h-7 w-7 p-0"
                                title="Approve"
                              >
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRejectDialog(ts.id)}
                                disabled={processing}
                                className="h-7 w-7 p-0"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Manual entry notes */}
                {group.entries.some(e => e.source === 'manual' && e.reason) && (
                  <div className="mt-3 pt-3 border-t border-[#3f3f46]/50">
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Manual entries require review
                    </p>
                    {group.entries.filter(e => e.source === 'manual' && e.reason).map(e => (
                      <p key={e.id} className="text-xs text-zinc-400 mt-1 pl-4">
                        {formatDate(e.date)}: "{e.reason}"
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[#1a1a1d] border-[#3f3f46] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani">
              Reject {rejectingIds.length > 1 ? `${rejectingIds.length} Timesheets` : 'Timesheet'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this timesheet is being rejected..."
                className="bg-[#09090b] border-[#3f3f46]"
                rows={3}
              />
              <p className="text-xs text-zinc-500">
                The officer will see this reason and can correct and resubmit.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              onClick={handleReject}
              disabled={processing}
              className="bg-red-500 hover:bg-red-600"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
